import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { useConfirm } from "@/components/ConfirmDialog";
import { Layout } from "@/components/Layout";
import { RichTextEditor, RichTextDisplay } from "@/components/RichTextEditor";
import { ImageDropZone } from "@/components/ImageDropZone";
import { SlidePanel } from "@/components/SlidePanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TierGate } from "@/components/TierGate";
import { Plus, Edit, Trash2, Skull, Search, ImageIcon, Upload, BookOpen, Lock, Shield, Heart, Check, Zap, Wand2, CheckSquare } from "lucide-react";
import VisibilityToggle from "@/components/VisibilityToggle";
import AICreateMode from "@/components/AICreateMode";
import { FolderRail, FolderToolbar, FolderCard, partitionCurrentLevel, useFolderCrud } from "@/components/FolderGrid";
import { SrdPortraitProgress } from "@/components/SrdPortraitProgress";
import { useMultiSelect, BulkActionBar, SelectCheckbox } from "@/components/MultiSelect";
import { CreatureStatBlock } from "@/components/CreatureStatBlock";
import EntityContextMenu from "@/components/EntityContextMenu";
import { FolderColorStrip } from "@/components/FolderColorStrip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CREATURE_TYPES = ["Aberration", "Beast", "Celestial", "Construct", "Dragon", "Elemental", "Fey", "Fiend", "Giant", "Humanoid", "Monstrosity", "Ooze", "Plant", "Undead"];
const CHALLENGE_RATINGS = ["0", "1/8", "1/4", "1/2", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12", "13", "14", "15", "16", "17", "18", "19", "20", "21", "22", "23", "24", "25", "26", "27", "28", "29", "30"];
const SIZES = ["Tiny", "Small", "Medium", "Large", "Huge", "Gargantuan"];
const ALIGNMENTS = ["Lawful Good", "Neutral Good", "Chaotic Good", "Lawful Neutral", "True Neutral", "Chaotic Neutral", "Lawful Evil", "Neutral Evil", "Chaotic Evil", "Unaligned"];

const defaultForm = {
  name: "", description: "", creature_type: "", challenge_rating: "",
  size: "", alignment: "", armor_class: "", hit_points: "", speed: "",
  str_score: 10, dex_score: 10, con_score: 10, int_score: 10, wis_score: 10, cha_score: 10,
  saving_throws: "", skills: "", damage_resistances: "", damage_immunities: "",
  condition_immunities: "", senses: "", languages: "",
  traits: [], actions: [], bonus_actions: [], reactions: [], legendary_actions: [], legendary_description: "",
  folder_id: "",
};

const abilityMod = (score) => {
  const mod = Math.floor((score - 10) / 2);
  return mod >= 0 ? `+${mod}` : `${mod}`;
};

const StatBlockSection = ({ title, items, onChange, addLabel }) => (
  <div>
    <div className="flex items-center justify-between mb-2">
      <label className="text-sm font-medium">{title}</label>
      <Button type="button" variant="ghost" size="sm" className="h-6 text-xs" onClick={() => onChange([...items, { name: "", description: "" }])}>
        <Plus className="w-3 h-3 mr-1" /> {addLabel || "Add"}
      </Button>
    </div>
    {items.map((item, i) => (
      <div key={i} className="flex gap-2 mb-2">
        <Input
          value={item.name}
          onChange={e => { const n = [...items]; n[i] = { ...n[i], name: e.target.value }; onChange(n); }}
          placeholder="Name"
          className="w-1/3"
        />
        <Input
          value={item.description}
          onChange={e => { const n = [...items]; n[i] = { ...n[i], description: e.target.value }; onChange(n); }}
          placeholder="Description"
          className="flex-1"
        />
        <Button type="button" variant="ghost" size="sm" className="h-9 px-2 text-destructive" onClick={() => onChange(items.filter((_, j) => j !== i))}>
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>
    ))}
  </div>
);

const CreaturesPage = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [campaign, setCampaign] = useState(null);
  const [creatures, setCreatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const confirm = useConfirm();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [uploadingImageFor, setUploadingImageFor] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedCreature, setSelectedCreature] = useState(null);
  const [folders, setFolders] = useState([]);
  const folderCrud = useFolderCrud({ entityType: "creatures", onRefresh: () => fetchData() });
  useEffect(() => { setFolders(folderCrud.folders); }, [folderCrud.folders]);
  const multi = useMultiSelect();

  const [formData, setFormData] = useState({ ...defaultForm });

  // SRD Monster Manual state
  const [activeTab, setActiveTab] = useState("bestiary");
  const [srdMonsters, setSrdMonsters] = useState([]);
  const [srdSearch, setSrdSearch] = useState("");
  const [srdFilterType, setSrdFilterType] = useState("all");
  const [srdFilterCR, setSrdFilterCR] = useState("all");
  const [srdLoading, setSrdLoading] = useState(false);
  const [addingMonster, setAddingMonster] = useState(null);
  const [addedMonsters, setAddedMonsters] = useState(new Set());
  const userTier = user?.tier || "commoner";
  const hasSrdAccess = userTier !== "commoner";
  const isCommoner = userTier === "commoner";

  useEffect(() => {
    fetchData();
  }, [campaignId]);

  const fetchData = async () => {
    try {
      const [campaignRes, creaturesRes] = await Promise.all([
        axios.get(`${API}/campaigns/${campaignId}`, { withCredentials: true }),
        axios.get(`${API}/campaigns/${campaignId}/creatures`, { withCredentials: true })
      ]);
      setCampaign(campaignRes.data);
      setCreatures(creaturesRes.data);
    } catch (error) {
      toast.error("Failed to load creatures");
    } finally {
      setLoading(false);
    }
  };

  const fetchSrdMonsters = async () => {
    if (!hasSrdAccess || srdMonsters.length > 0) return;
    setSrdLoading(true);
    try {
      const res = await axios.get(`${API}/srd-monsters`, { withCredentials: true });
      setSrdMonsters(res.data);
    } catch { toast.error("Failed to load Monster Manual"); }
    finally { setSrdLoading(false); }
  };

  const addSrdToBestiary = async (monster) => {
    setAddingMonster(monster.id);
    try {
      await axios.post(`${API}/srd-monsters/add-to-campaign`, { campaign_id: campaignId, srd_id: monster.id }, { withCredentials: true });
      toast.success(`${monster.name} added to your Bestiary`);
      setAddedMonsters(prev => new Set(prev).add(monster.id));
      fetchData();
    } catch { toast.error("Failed to add monster"); }
    finally { setAddingMonster(null); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        campaign_id: campaignId,
        folder_id: formData.folder_id === "none" || !formData.folder_id ? null : formData.folder_id,
      };
      let charId;
      
      if (editing) {
        await axios.put(`${API}/creatures/${editing.id}`, payload, { withCredentials: true });
        charId = editing.id;
        toast.success("Creature updated");
      } else {
        const res = await axios.post(`${API}/creatures`, payload, { withCredentials: true });
        charId = res.data.id;
        toast.success("Creature created");
      }
      if (imageFile && charId) {
        const fd = new FormData();
        fd.append("file", imageFile);
        await axios.post(`${API}/creatures/${charId}/upload`, fd, {
          withCredentials: true, headers: { "Content-Type": "multipart/form-data" }
        });
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error("Failed to save creature");
    }
  };

  const handleDelete = async (id) => {
    const ok = await confirm("Delete Creature", "Are you sure you want to delete this creature?");
    if (!ok) return;
    try {
      await axios.delete(`${API}/creatures/${id}`, { withCredentials: true });
      toast.success("Creature deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete creature");
    }
  };

  const handleImageUpload = async (creatureId, file) => {
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    setUploadingImageFor(creatureId);
    try {
      await axios.post(`${API}/creatures/${creatureId}/upload`, formDataUpload, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success("Image uploaded");
      fetchData();
    } catch (error) {
      toast.error("Failed to upload image");
    } finally {
      setUploadingImageFor(null);
    }
  };

  const getImageUrl = (creature) => {
    if (creature.image_path) {
      return `${API}/files/${creature.image_path}`;
    }
    return null;
  };

  const resetForm = () => {
    setEditing(null);
    setImageFile(null);
    setImagePreview(null);
    setFormData({ ...defaultForm, folder_id: folderCrud.currentFolderId || "" });
  };

  const handleFormImageSelect = (file) => {
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const openEdit = (creature) => {
    setEditing(creature);
    setImageFile(null);
    setImagePreview(getImageUrl(creature));
    setFormData({
      name: creature.name || "",
      description: creature.description || "",
      creature_type: creature.creature_type || "",
      challenge_rating: creature.challenge_rating || "",
      size: creature.size || "",
      alignment: creature.alignment || "",
      armor_class: creature.armor_class || "",
      hit_points: creature.hit_points || "",
      speed: creature.speed || "",
      str_score: creature.str_score ?? 10,
      dex_score: creature.dex_score ?? 10,
      con_score: creature.con_score ?? 10,
      int_score: creature.int_score ?? 10,
      wis_score: creature.wis_score ?? 10,
      cha_score: creature.cha_score ?? 10,
      saving_throws: creature.saving_throws || "",
      skills: creature.skills || "",
      damage_resistances: creature.damage_resistances || "",
      damage_immunities: creature.damage_immunities || "",
      condition_immunities: creature.condition_immunities || "",
      senses: creature.senses || "",
      languages: creature.languages || "",
      traits: creature.traits || [],
      actions: creature.actions || [],
      bonus_actions: creature.bonus_actions || [],
      reactions: creature.reactions || [],
      legendary_actions: creature.legendary_actions || [],
      legendary_description: creature.legendary_description || "",
      folder_id: creature.folder_id || "",
    });
    setDialogOpen(true);
  };

  const openCreate = () => {
    resetForm();
    setCreateMode("manual");
    setDialogOpen(true);
  };
  const [createMode, setCreateMode] = useState("manual");

  const CR_ORDER = ["0","1/8","1/4","1/2","1","2","3","4","5","6","7","8","9","10","11","12","13","14","15","16","17","18","19","20","21","22","23","24","25","26","27","28","29","30"];

  const filteredSrd = srdMonsters.filter(m => {
    const matchSearch = m.name.toLowerCase().includes(srdSearch.toLowerCase());
    const matchType = srdFilterType === "all" || m.creature_type === srdFilterType;
    const matchCR = srdFilterCR === "all" || m.challenge_rating === srdFilterCR;
    return matchSearch && matchType && matchCR;
  });

  const filteredCreatures = creatures.filter(c => {
    const matchesSearch = c.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === "all" || c.creature_type === filterType;
    return matchesSearch && matchesType;
  });
  const filteredAll = filteredCreatures;
  const { folders: levelFolders, items: filteredByFolderCreatures } = partitionCurrentLevel({
    folders, items: filteredAll, currentFolderId: folderCrud.currentFolderId
  });

  const moveCreatureToFolder = async (creatureId, folderId) => {
    try {
      await axios.put(`${API}/campaigns/${campaignId}/creatures/${creatureId}/folder`, { folder_id: folderId }, { withCredentials: true });
      setCreatures(prev => prev.map(c => c.id === creatureId ? { ...c, folder_id: folderId } : c));
    } catch { toast.error("Failed to move creature"); }
  };

  const generateCreatureImage = async (creatureId) => {
    const c = creatures.find(x => x.id === creatureId);
    if (!c) return;
    try {
      toast.info("Generating AI image...");
      await axios.post(`${API}/creatures/${creatureId}/generate-image`, { prompt: `D&D fantasy monster illustration of ${c.name}, ${c.creature_type || ""} ${c.size || ""}, ${(c.description || "").substring(0, 200)}, dark fantasy bestiary art style, detailed, atmospheric, no text` }, { withCredentials: true });
      toast.success("Image generating — will appear shortly");
      setTimeout(fetchCreatures, 5000);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to generate image"); }
  };

  const bulkMoveCreatures = async (folderId) => {
    const ids = [...multi.selectedIds];
    try {
      await Promise.all(ids.map(id => axios.put(`${API}/campaigns/${campaignId}/creatures/${id}/folder`, { folder_id: folderId }, { withCredentials: true })));
      setCreatures(prev => prev.map(c => ids.includes(c.id) ? { ...c, folder_id: folderId } : c));
      multi.clearAll();
      toast.success(`Moved ${ids.length} creatures`);
    } catch { toast.error("Failed"); }
  };

  const bulkDeleteCreatures = async () => {
    const ids = [...multi.selectedIds];
    if (!window.confirm(`Delete ${ids.length} creatures?`)) return;
    try {
      await Promise.all(ids.map(id => axios.delete(`${API}/creatures/${id}`, { withCredentials: true })));
      setCreatures(prev => prev.filter(c => !ids.includes(c.id)));
      multi.clearAll();
      toast.success(`Deleted ${ids.length} creatures`);
    } catch { toast.error("Failed"); }
  };

  if (loading) {
    return (
      <Layout campaignName="Loading...">
        <div className="flex justify-center py-20">
          <div className="spinner"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout campaignName={campaign?.name}>
      <div data-testid="creatures-page">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-serif text-foreground">Bestiary</h1>
            <p className="text-muted-foreground text-sm">Catalog monsters and creatures your party encounters</p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant={multi.selectMode ? "default" : "outline"} className="rounded-sm text-xs" onClick={() => multi.setSelectMode(!multi.selectMode)} data-testid="toggle-select-mode">
              <CheckSquare className="w-3.5 h-3.5 mr-1" /> {multi.selectMode ? "Cancel" : "Select"}
            </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                onClick={openCreate}
                data-testid="create-creature-button"
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Creature
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl">
                  {editing ? "Edit Creature" : "New Creature"}
                </DialogTitle>
              </DialogHeader>
              {!editing && (
                <div className="flex gap-2 border-b border-border pb-3 mb-1">
                  <Button variant={createMode === "manual" ? "default" : "outline"} size="sm" className="rounded-sm text-xs" onClick={() => setCreateMode("manual")} data-testid="mode-manual">
                    <Plus className="w-3 h-3 mr-1" /> Create Manually
                  </Button>
                  {!isCommoner && (
                    <Button variant={createMode === "ai" ? "default" : "outline"} size="sm" className="rounded-sm text-xs" onClick={() => setCreateMode("ai")} data-testid="mode-ai">
                      <Wand2 className="w-3 h-3 mr-1" /> Use AI
                    </Button>
                  )}
                  {isCommoner && (
                    <Button variant="outline" size="sm" className="rounded-sm text-xs opacity-60 cursor-not-allowed" disabled data-testid="mode-ai-locked">
                      <Lock className="w-3 h-3 mr-1" /> AI (Upgrade)
                    </Button>
                  )}
                </div>
              )}
              {!editing && createMode === "ai" && (
                <AICreateMode entityType="creature" campaignId={campaignId} onGenerated={(data) => {
                  setFormData(prev => ({ ...prev, ...data }));
                  setCreateMode("manual");
                }} />
              )}
              <form onSubmit={handleSubmit} className="space-y-4 mt-4" style={{ display: (!editing && createMode === "ai") ? "none" : undefined }}>
                {/* Image Upload */}
                <div>
                  <label className="text-sm font-medium block mb-2">Creature Image</label>
                  <ImageDropZone
                    compact
                    onFileSelect={handleFormImageSelect}
                    preview={imagePreview}
                    fileName={imageFile?.name}
                    testId="creature-image-picker"
                    onClear={() => { setImageFile(null); setImagePreview(null); }}
                  />
                </div>

                {/* Name */}
                <div>
                  <label className="text-sm font-medium block mb-1">Name *</label>
                  <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Ancient Red Dragon" required data-testid="creature-name-input" />
                </div>

                {/* Type, Size, Alignment row */}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-sm font-medium block mb-1">Type</label>
                    <Select value={formData.creature_type || "__none__"} onValueChange={(v) => setFormData({ ...formData, creature_type: v === "__none__" ? "" : v })}>
                      <SelectTrigger data-testid="creature-type-select"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{CREATURE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium block mb-1">Size</label>
                    <Select value={formData.size || "__none__"} onValueChange={(v) => setFormData({ ...formData, size: v === "__none__" ? "" : v })}>
                      <SelectTrigger data-testid="creature-size-select"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{SIZES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium block mb-1">Alignment</label>
                    <Select value={formData.alignment || "__none__"} onValueChange={(v) => setFormData({ ...formData, alignment: v === "__none__" ? "" : v })}>
                      <SelectTrigger data-testid="creature-alignment-select"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{ALIGNMENTS.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>

                {/* AC, HP, Speed, CR */}
                <div className="grid grid-cols-4 gap-3">
                  <div>
                    <label className="text-sm font-medium block mb-1">AC</label>
                    <Input value={formData.armor_class} onChange={e => setFormData({ ...formData, armor_class: e.target.value })} placeholder="18" data-testid="creature-ac" />
                  </div>
                  <div>
                    <label className="text-sm font-medium block mb-1">HP</label>
                    <Input value={formData.hit_points} onChange={e => setFormData({ ...formData, hit_points: e.target.value })} placeholder="256 (19d12+133)" data-testid="creature-hp" />
                  </div>
                  <div>
                    <label className="text-sm font-medium block mb-1">Speed</label>
                    <Input value={formData.speed} onChange={e => setFormData({ ...formData, speed: e.target.value })} placeholder="40 ft., fly 80 ft." data-testid="creature-speed" />
                  </div>
                  <div>
                    <label className="text-sm font-medium block mb-1">CR</label>
                    <Select value={formData.challenge_rating || "__none__"} onValueChange={(v) => setFormData({ ...formData, challenge_rating: v === "__none__" ? "" : v })}>
                      <SelectTrigger data-testid="creature-cr-select"><SelectValue placeholder="CR" /></SelectTrigger>
                      <SelectContent className="max-h-60">{CHALLENGE_RATINGS.map(cr => <SelectItem key={cr} value={cr}>{cr}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Ability Scores */}
                <div>
                  <label className="text-sm font-medium block mb-2">Ability Scores</label>
                  <div className="grid grid-cols-6 gap-2">
                    {[["STR","str_score"],["DEX","dex_score"],["CON","con_score"],["INT","int_score"],["WIS","wis_score"],["CHA","cha_score"]].map(([label, key]) => (
                      <div key={key} className="text-center">
                        <span className="text-[10px] font-bold uppercase text-muted-foreground">{label}</span>
                        <Input
                          type="number"
                          min={1}
                          max={30}
                          value={formData[key]}
                          onChange={e => setFormData({ ...formData, [key]: parseInt(e.target.value) || 10 })}
                          className="text-center h-9 mt-0.5"
                          data-testid={`creature-${key}`}
                        />
                        <span className="text-xs text-muted-foreground">{abilityMod(formData[key])}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Defences & Senses */}
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium block mb-1">Saving Throws</label>
                      <Input value={formData.saving_throws} onChange={e => setFormData({ ...formData, saving_throws: e.target.value })} placeholder="Dex +7, Con +13, Wis +9" className="h-8 text-xs" />
                    </div>
                    <div>
                      <label className="text-xs font-medium block mb-1">Skills</label>
                      <Input value={formData.skills} onChange={e => setFormData({ ...formData, skills: e.target.value })} placeholder="Perception +16, Stealth +7" className="h-8 text-xs" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium block mb-1">Damage Resistances</label>
                      <Input value={formData.damage_resistances} onChange={e => setFormData({ ...formData, damage_resistances: e.target.value })} placeholder="Bludgeoning, piercing..." className="h-8 text-xs" />
                    </div>
                    <div>
                      <label className="text-xs font-medium block mb-1">Damage Immunities</label>
                      <Input value={formData.damage_immunities} onChange={e => setFormData({ ...formData, damage_immunities: e.target.value })} placeholder="Fire" className="h-8 text-xs" />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs font-medium block mb-1">Condition Immunities</label>
                      <Input value={formData.condition_immunities} onChange={e => setFormData({ ...formData, condition_immunities: e.target.value })} placeholder="Frightened" className="h-8 text-xs" />
                    </div>
                    <div>
                      <label className="text-xs font-medium block mb-1">Senses</label>
                      <Input value={formData.senses} onChange={e => setFormData({ ...formData, senses: e.target.value })} placeholder="Blindsight 60 ft." className="h-8 text-xs" />
                    </div>
                    <div>
                      <label className="text-xs font-medium block mb-1">Languages</label>
                      <Input value={formData.languages} onChange={e => setFormData({ ...formData, languages: e.target.value })} placeholder="Common, Draconic" className="h-8 text-xs" />
                    </div>
                  </div>
                </div>

                {/* Stat Block Sections */}
                <div className="border-t border-border pt-4 space-y-4">
                  <StatBlockSection title="Traits" items={formData.traits} onChange={v => setFormData({ ...formData, traits: v })} addLabel="Add Trait" />
                  <StatBlockSection title="Actions" items={formData.actions} onChange={v => setFormData({ ...formData, actions: v })} addLabel="Add Action" />
                  <StatBlockSection title="Bonus Actions" items={formData.bonus_actions} onChange={v => setFormData({ ...formData, bonus_actions: v })} addLabel="Add Bonus Action" />
                  <StatBlockSection title="Reactions" items={formData.reactions} onChange={v => setFormData({ ...formData, reactions: v })} addLabel="Add Reaction" />
                  <div>
                    <StatBlockSection title="Legendary Actions" items={formData.legendary_actions} onChange={v => setFormData({ ...formData, legendary_actions: v })} addLabel="Add Legendary" />
                    {formData.legendary_actions.length > 0 && (
                      <Input value={formData.legendary_description} onChange={e => setFormData({ ...formData, legendary_description: e.target.value })} placeholder="The creature can take 3 legendary actions..." className="mt-2 text-xs h-8" />
                    )}
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="text-sm font-medium block mb-1">Description / Lore</label>
                  <RichTextEditor value={formData.description} onChange={(value) => setFormData({ ...formData, description: value })} placeholder="Describe this creature..." />
                </div>

                {/* Folder */}
                <div>
                  <label className="text-sm font-medium block mb-1">Folder</label>
                  <Select value={formData.folder_id || "none"} onValueChange={v => setFormData({ ...formData, folder_id: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="creature-folder-select"><SelectValue placeholder="Unfiled" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Unfiled</SelectItem>
                      {folders.filter(f => !f.archived).sort((a, b) => a.name.localeCompare(b.name)).map(f => (
                        <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button type="submit" data-testid="save-creature-button" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm">
                  {editing ? "Save Changes" : "Create Creature"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); if (v === "manual") fetchSrdMonsters(); }} className="w-full">
          <TabsList className="bg-muted mb-6">
            <TabsTrigger value="bestiary" data-testid="tab-bestiary">My Bestiary</TabsTrigger>
            <TabsTrigger value="manual" data-testid="tab-monster-manual">
              <BookOpen className="w-3.5 h-3.5 mr-1.5" /> Monster Manual
              {!hasSrdAccess && <Lock className="w-3 h-3 ml-1.5 text-muted-foreground" />}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="bestiary">
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search creatures..."
              className="pl-10 bg-input border-border rounded-sm"
              data-testid="search-creatures"
            />
          </div>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-[180px] bg-input border-border rounded-sm" data-testid="filter-creature-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-popover border-border">
              <SelectItem value="all">All Types</SelectItem>
              {CREATURE_TYPES.map(type => (
                <SelectItem key={type} value={type}>{type}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Folder browser */}
        <div className="flex gap-4 items-start">
          <FolderRail folders={folders} items={creatures} currentFolderId={folderCrud.currentFolderId} onNavigate={folderCrud.setCurrentFolderId} allLabel="All Creatures" className="w-44 shrink-0 sticky top-2 self-start" testIdPrefix="creatures" />
          <div className="flex-1 min-w-0">
            <FolderToolbar folders={folders} currentFolderId={folderCrud.currentFolderId} onNavigate={folderCrud.setCurrentFolderId} onNewFolder={folderCrud.createHere} allLabel="All Creatures" className="mb-3" />
            {levelFolders.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3 mb-4" data-testid="creatures-folder-grid">
                {levelFolders.map(f => (
                  <FolderCard key={f.id} folder={f}
                    count={creatures.filter(c => c.folder_id === f.id).length + folders.filter(x => x.parent_id === f.id && !x.archived).length}
                    onOpen={() => folderCrud.setCurrentFolderId(f.id)}
                    onRename={() => folderCrud.renameFolder(f)}
                    onDelete={() => folderCrud.deleteFolder(f)}
                    onArchive={() => folderCrud.toggleArchive(f)}
                    onColor={(c) => folderCrud.setFolderColor(f, c)}
                    onDropItem={folderCrud.moveItem} />
                ))}
              </div>
            )}

        {creatures.length === 0 ? (
          <div className="empty-state">
            <Skull className="empty-state-icon text-muted-foreground" />
            <h3 className="text-xl font-serif text-foreground mb-2">No creatures yet</h3>
            <p className="text-muted-foreground mb-6">Add monsters to your bestiary</p>
            <Button 
              onClick={openCreate}
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Creature
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4">
            {filteredByFolderCreatures.map((creature) => (
              <EntityContextMenu
                key={creature.id}
                onOpen={() => setSelectedCreature(creature)}
                onRename={() => openEdit(creature)}
                onDelete={() => handleDelete(creature.id)}
                folders={folders}
                currentFolderId={creature.folder_id}
                onMoveFolder={(fid) => moveCreatureToFolder(creature.id, fid)}
                pinPath="creatures"
                pinLabel={`Creature: ${creature.name}`}
                entityName={creature.name}
              >
              <Card
                className={`bg-card border-border rounded-sm cursor-pointer entity-card overflow-hidden relative ${multi.isSelected(creature.id) ? "ring-2 ring-primary" : ""}`}
                onClick={() => multi.selectMode ? multi.toggle(creature.id) : setSelectedCreature(creature)}
                draggable
                onDragStart={e => e.dataTransfer.setData("entityId", creature.id)}
                data-testid={`creature-card-${creature.id}`}
              >
                <FolderColorStrip entityId={creature.id} folderId={creature.folder_id} folders={folders} onFilter={folderCrud.setCurrentFolderId} testIdPrefix="creature" />
                {multi.selectMode && <SelectCheckbox checked={multi.isSelected(creature.id)} onChange={() => multi.toggle(creature.id)} />}
                {getImageUrl(creature) && (
                  <div className="h-36 w-full overflow-hidden">
                    <img
                      src={getImageUrl(creature)}
                      alt={creature.name}
                      className="w-full h-full object-cover"
                      crossOrigin="use-credentials"
                      data-testid={`creature-image-${creature.id}`}
                    />
                  </div>
                )}
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center gap-3">
                      {!getImageUrl(creature) && (
                        <div className="w-10 h-10 rounded-sm bg-secondary/20 flex items-center justify-center">
                          <Skull className="w-5 h-5 text-secondary" />
                        </div>
                      )}
                      <div>
                        <h3 className="font-serif text-lg text-foreground">{creature.name}</h3>
                        <p className="text-xs text-muted-foreground">{creature.creature_type || "Unknown type"}</p>
                      </div>
                    </div>
                    <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                      <VisibilityToggle
                        entityType="creatures"
                        entityId={creature.id}
                        currentVisibility={creature.visibility || "dm_only"}
                        onUpdate={(v) => setCreatures(prev => prev.map(c => c.id === creature.id ? {...c, visibility: v} : c))}
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        disabled={uploadingImageFor === creature.id}
                        onClick={() => {
                          const input = document.createElement('input');
                          input.type = 'file';
                          input.accept = 'image/*';
                          input.onchange = (e) => {
                            const file = e.target.files?.[0];
                            if (file) handleImageUpload(creature.id, file);
                          };
                          input.click();
                        }}
                        data-testid={`upload-creature-image-${creature.id}`}
                      >
                        {uploadingImageFor === creature.id ? (
                          <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <ImageIcon className="w-4 h-4" />
                        )}
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(creature)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(creature.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  {creature.challenge_rating && (
                    <p className="text-sm text-muted-foreground">Challenge Rating: {creature.challenge_rating}</p>
                  )}
                </CardContent>
              </Card>
              </EntityContextMenu>
            ))}
          </div>
        )}
          </div>
        </div>
          </TabsContent>

          {/* ==================== MONSTER MANUAL TAB ==================== */}
          <TabsContent value="manual">
            <TierGate feature="Monster Manual" description="Browse 274 pre-built D&D monsters from the SRD. Add them to your Bestiary and use them in the Encounter Builder.">
            <SrdPortraitProgress />
            {srdLoading ? (
              <div className="flex justify-center py-20"><div className="spinner" /></div>
            ) : (
              <>
                {/* SRD Filters */}
                <div className="flex flex-col sm:flex-row gap-3 mb-6">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      value={srdSearch}
                      onChange={(e) => setSrdSearch(e.target.value)}
                      placeholder="Search monsters..."
                      className="pl-10 bg-input border-border rounded-sm"
                      data-testid="srd-search"
                    />
                  </div>
                  <Select value={srdFilterType} onValueChange={setSrdFilterType}>
                    <SelectTrigger className="w-[160px] bg-input border-border rounded-sm" data-testid="srd-filter-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-popover border-border">
                      <SelectItem value="all">All Types</SelectItem>
                      {CREATURE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={srdFilterCR} onValueChange={setSrdFilterCR}>
                    <SelectTrigger className="w-[120px] bg-input border-border rounded-sm" data-testid="srd-filter-cr">
                      <SelectValue placeholder="All CRs" />
                    </SelectTrigger>
                    <SelectContent className="bg-popover border-border max-h-60">
                      <SelectItem value="all">All CRs</SelectItem>
                      {CR_ORDER.map(cr => <SelectItem key={cr} value={cr}>CR {cr}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <p className="text-xs text-muted-foreground mb-4">
                  {filteredSrd.length} of {srdMonsters.length} monsters &mdash; Click "Add" to copy to your campaign Bestiary
                </p>

                {/* SRD Monster Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-3">
                  {filteredSrd.map(m => {
                    const isAdded = addedMonsters.has(m.id);
                    const isAdding = addingMonster === m.id;
                    return (
                      <Card key={m.id} className="bg-card border-border rounded-sm" data-testid={`srd-monster-${m.id}`}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between gap-3">
                            {/* Portrait */}
                            <div className="w-14 h-14 rounded-sm overflow-hidden border border-border bg-muted/30 flex-shrink-0 flex items-center justify-center">
                              {m.image_path ? (
                                <img src={`${API}/files/${m.image_path}`} alt={m.name} className="w-full h-full object-cover" crossOrigin="use-credentials" />
                              ) : (
                                <Skull className="w-5 h-5 text-muted-foreground/40" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-serif text-base text-foreground truncate">{m.name}</h4>
                              <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground flex-wrap">
                                <span className="capitalize">{m.creature_type}</span>
                                <span className="flex items-center gap-0.5"><Shield className="w-3 h-3" /> AC {m.armor_class}</span>
                                <span className="flex items-center gap-0.5"><Heart className="w-3 h-3" /> {m.hit_points} HP</span>
                                <span className="text-primary font-medium">CR {m.challenge_rating}</span>
                              </div>
                              <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{m.description}</p>
                            </div>
                            <div className="flex flex-col gap-1 flex-shrink-0">
                              <Button
                                variant={isAdded ? "ghost" : "outline"}
                                size="sm"
                                className={`h-8 rounded-sm ${isAdded ? "text-green-400" : "border-border"}`}
                                disabled={isAdding}
                                onClick={() => addSrdToBestiary(m)}
                                data-testid={`add-srd-${m.id}`}
                              >
                                {isAdding ? (
                                  <div className="w-3.5 h-3.5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                                ) : isAdded ? (
                                  <><Check className="w-3.5 h-3.5 mr-1" /> Added</>
                                ) : (
                                  <><Plus className="w-3.5 h-3.5 mr-1" /> Add</>
                                )}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 text-xs text-primary hover:text-primary/80"
                                onClick={async () => {
                                  // Quick Encounter: add to bestiary if not added, then navigate
                                  if (!isAdded) await addSrdToBestiary(m);
                                  navigate(`/campaign/${campaignId}/encounter-builder?add=${encodeURIComponent(m.name)}`);
                                }}
                                data-testid={`quick-encounter-${m.id}`}
                              >
                                <Zap className="w-3 h-3 mr-1" /> Quick Encounter
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </>
            )}
            </TierGate>
          </TabsContent>
        </Tabs>

        {/* Creature Detail SlidePanel */}
        <SlidePanel open={!!selectedCreature} onClose={() => setSelectedCreature(null)} title={selectedCreature?.name || "Creature"}
          items={filteredCreatures} currentIndex={selectedCreature ? filteredCreatures.findIndex(c => c.id === selectedCreature.id) : -1}
          onNavigate={(i) => setSelectedCreature(filteredCreatures[i])}
        >
          {selectedCreature && (
            <div data-testid="creature-detail-panel">
              <CreatureStatBlock creature={selectedCreature} getImageUrl={getImageUrl} />

              <div className="flex flex-wrap gap-2 pt-4 mt-4 border-t border-border">
                <Button variant="outline" size="sm" className="flex-1 rounded-sm" onClick={() => { setSelectedCreature(null); openEdit(selectedCreature); }} data-testid="creature-detail-edit">
                  <Edit className="w-3.5 h-3.5 mr-1.5" /> Edit
                </Button>
                <Button variant="outline" size="sm" className="rounded-sm" onClick={() => generateCreatureImage(selectedCreature.id)} data-testid="creature-generate-image">
                  <Wand2 className="w-3.5 h-3.5 mr-1.5" /> AI Image
                </Button>
                <Button variant="outline" size="sm" className="rounded-sm" onClick={() => { setUploadingImageFor(selectedCreature.id); document.querySelector('input[type="file"]')?.click(); }} data-testid="creature-detail-upload">
                  <Upload className="w-3.5 h-3.5 mr-1.5" /> Upload
                </Button>
                <Button variant="ghost" size="sm" className="text-destructive rounded-sm" onClick={() => handleDelete(selectedCreature.id)} data-testid="creature-detail-delete">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          )}
        </SlidePanel>
      </div>
      <BulkActionBar count={multi.count} folders={folders} onBulkMove={bulkMoveCreatures} onBulkDelete={bulkDeleteCreatures} onClear={multi.clearAll} />
    </Layout>
  );
};

export default CreaturesPage;
